import React from 'react';
import { AppLayout } from '@heroui-pro/react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getNavigationBackTarget, getNavigationTitle } from '../navigation';
import { DashboardNavbar } from './DashboardNavbar';
import { DashboardSidebar } from './DashboardSidebar';

type AppShellProps = {
    children: React.ReactNode;
    showBack: boolean;
};

export const AppShell: React.FC<AppShellProps> = ({ children, showBack }) => {
    const { t } = useTranslation('components');
    const location = useLocation();
    const navigate = useNavigate();

    const title = React.useMemo(
        () => getNavigationTitle(location.pathname, t),
        [location.pathname, t],
    );

    const navigateTo = React.useCallback(
        (href: string) => navigate(href),
        [navigate],
    );
    const navigateBack = React.useCallback(() => {
        // A fixed back target (see getNavigationBackTarget) wins over history:
        // back always leaves to the parent section, never to a previous form
        // state, and saves nothing on the way out.
        const backTarget = getNavigationBackTarget(location.pathname);
        if (backTarget !== null) {
            navigate(backTarget);
            return;
        }
        navigate(-1);
    }, [location.pathname, navigate]);

    return (
        <div
            className="min-h-screen bg-background"
            data-testid="app-shell-root"
            id="app-shell-root"
        >
            <AppLayout
                data-testid="app-shell-layout"
                id="app-shell-layout"
                navbar={
                    <DashboardNavbar
                        onBack={navigateBack}
                        showBack={showBack}
                        title={title}
                    />
                }
                navigate={navigateTo}
                sidebar={<DashboardSidebar pathname={location.pathname} />}
                sidebarCollapsible="offcanvas"
                sidebarVariant="floating"
            >
                <div
                    className="flex w-full justify-start px-4 py-6 sm:pl-8"
                    data-testid="app-shell-main"
                    id="app-shell-main"
                >
                    <div
                        className="w-full max-w-7xl"
                        data-testid="app-shell-content"
                        id="app-shell-content"
                    >
                        {children}
                    </div>
                </div>
            </AppLayout>
        </div>
    );
};
